import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { FormBuilder,  Validators } from '@angular/forms';
import { UsuarioService } from 'src/app/services/usuario.service';
import { DomSanitizer } from '@angular/platform-browser';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource, Photo } from '@capacitor/camera';
import { API_URI } from 'src/environments/environment';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';
import { firstValueFrom } from 'rxjs';
import * as bootstrap from 'bootstrap';

@Component({
    selector: 'app-my-account-obra-social',
    templateUrl: './my-account-obra-social.component.html',
    styleUrls: ['./my-account-obra-social.component.css'],
    standalone: false
})
export class MyAccountObraSocialComponent implements OnInit {
  private _router = inject(Router);
  private _location = inject(Location);
  private _fb = inject(FormBuilder);
  private _usuarioService = inject(UsuarioService);
  private _authService = inject(AuthService);
  private _loadingService = inject(LoadingService);
  private sanitizer = inject(DomSanitizer);

  // estado
  error = signal<string>('');
  loading = computed(() => this._loadingService.isLoading());

  obraSocialSeleccionada = signal<string>(''); // solo texto (la selección real es id_obra_social en el service)
  preview_img_credencial_obra_social = signal<string>('');

  // archivo
  private x_img_credencial_obra_social?: File;
  private previewCredencialOriginal = '';

  private modalInstance?: any;
  private modalConfirmarEliminarInstance?: any;

  // form
  readonly form = this._fb.nonNullable.group({
    obra_social: this._fb.nonNullable.control('', [Validators.required]),
    obra_social_plan: this._fb.nonNullable.control('', [Validators.required]),
  });

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    // asegura sesión/usuario (misma estrategia que venís usando)
    await this._loadingService.run(async () => {
      try {
        await this._authService.ensureSession();
      } catch (e: any) {
        console.error(e);
        this.error.set(e?.message || 'No se pudo cargar tu cuenta.');
        this._router.navigate(['/sign-in']);
      }
    });

    // Cargar valores iniciales desde el service
    const cli = this._usuarioService.cliente();
    const idOS = cli?.id_obra_social ?? null;

    const osDesc =
      idOS
        ? (this._usuarioService.obrasSociales?.find(x => x.id === idOS)?.x_descripcion ?? '')
        : '';

    this.obraSocialSeleccionada.set(osDesc);

    if (cli?.x_img_credencial_obra_social) {
      const img = `${API_URI}uploads/images/${cli.x_img_credencial_obra_social}`;
      this.preview_img_credencial_obra_social.set(img);
      this.previewCredencialOriginal = img;
    } else {
      this.preview_img_credencial_obra_social.set('');
      this.previewCredencialOriginal = '';
    }

    this.form.patchValue({
      obra_social: osDesc,
      obra_social_plan: cli?.x_obra_social_plan?.trim() ?? '',
    });

    // Cerrar modal si el usuario navega hacia atrás
    this._location.subscribe(() => {
      const modalEl = document.getElementById('modalPreviewImg');
      if (modalEl && this.modalInstance) {
        this.modalInstance.hide();
      }
    });
  }

  async saveObraSocial(): Promise<void> {
    if (this.loading() || this.form.pending) return;

    this.error.set('');

    // Validaciones
    this.form.markAllAsTouched();
    const cli = this._usuarioService.cliente();
    const idOS = cli?.id_obra_social ?? 0;

    if (!idOS) {
      this.error.set('Debe Seleccionar la Obra Social');
      return;
    }

    if (this.form.invalid) {
      this.error.set('Debe completar los campos requeridos.');
      return;
    }

    const v = this.form.getRawValue();
    if (!v.obra_social_plan?.trim()) {
      this.error.set('Debe Completar el plan de la Obra Social');
      return;
    }

    await this._loadingService.run(async () => {
      try {
        // 1) Actualizar datos en el service (reactivo)
        const c = { ...(this._usuarioService.cliente() ?? {}) } as any;
        c.x_obra_social_plan = v.obra_social_plan.trim();
        this._usuarioService.cliente.set(c);

        // 2) Subir credencial (si cambió)
        if (this.x_img_credencial_obra_social) {
          const res: any = await firstValueFrom(
            this._usuarioService.updateClienteImgCredencialObraSocial(this.x_img_credencial_obra_social)
          );
          const c2 = { ...(this._usuarioService.cliente() ?? {}) } as any;
          c2.x_img_credencial_obra_social = res?.imagen ?? c2.x_img_credencial_obra_social;
          this._usuarioService.cliente.set(c2);
        }

        // 3) Persistir cliente
        await firstValueFrom(this._usuarioService.updateCliente(this._usuarioService.cliente()));

        await this._router.navigate(['/my-account']);
      } catch (e) {
        console.error(e);
        this.error.set('Error al actualizar el usuario');
      }
    });
  }

  ngOnDestroy(): void {
    if (this.modalInstance) this.modalInstance.hide();
    if (this.modalConfirmarEliminarInstance) this.modalConfirmarEliminarInstance.hide();
  }

  // -------- Modal foto perfil --------
    openModal() {
      const modalEl = document.getElementById('modalPreviewImg');
      if (modalEl) {
        this.modalInstance = new (bootstrap as any).Modal(modalEl);
        this.modalInstance.show();
      }
    }

  // -------- Imagen credencial --------

  private async pickPhoto(fileName: string): Promise<{ file: File; dataUrl: string }> {
    // En web conviene Photos; en nativo Prompt (permite Cámara o Galería)
    const source = Capacitor.getPlatform() === 'web' ? CameraSource.Photos : CameraSource.Prompt;

    const photo: Photo = await Camera.getPhoto({
      source,
      resultType: CameraResultType.DataUrl,
      quality: 90,
      allowEditing: false,
      promptLabelHeader: 'Seleccionar imagen',
      promptLabelPhoto: 'Galería',
      promptLabelPicture: 'Cámara',
    });

    // Asegurar un webPath o dataUrl/base64
    const file = await this.photoToFile(photo, fileName);
    const dataUrl = await this.fileToDataUrl(file);
    return { file, dataUrl };
  }

  /** Convierte Photo de Capacitor a File */
  private async photoToFile(photo: Photo, fileName: string): Promise<File> {
    let blob: Blob | null = null;

    if (photo.webPath) {
      const res = await fetch(photo.webPath);
      blob = await res.blob();
    } else if (photo.dataUrl) {
      const res = await fetch(photo.dataUrl);
      blob = await res.blob();
    } else if (photo.base64String) {
      const byteString = atob(photo.base64String);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
      blob = new Blob([ab], { type: 'image/jpeg' });
    }

    if (!blob) throw new Error('No se pudo leer la imagen.');
    return new File([blob], fileName, { type: blob.type || 'image/jpeg' });
  }

  /** Convierte File a DataURL (para preview) */
  private async fileToDataUrl(file: File): Promise<string> {
    return await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => resolve('');
      reader.readAsDataURL(file);
    });
  }

  async capturarFileImgCredencialObraSocial(): Promise<void> {
    try {
      const { file, dataUrl } = await this.pickPhoto('obra-social-credencial.jpg');
      this.x_img_credencial_obra_social = file;
      this.preview_img_credencial_obra_social.set(dataUrl);
    } catch (e) {
      console.error('Error al capturar imagen:', e);
    }
  }

  // -------- Eliminar credencial --------
  eliminarImgCredencialObraSocial(): void {
    const modalEl = document.getElementById('modalConfirmarEliminarImagen');
    if (modalEl) {
      this.modalConfirmarEliminarInstance = new (bootstrap as any).Modal(modalEl);
      this.modalConfirmarEliminarInstance.show();
    }
  }

  async confirmarEliminarImgCredencialObraSocial(): Promise<void> {
    this.modalConfirmarEliminarInstance?.hide();

    if (this.x_img_credencial_obra_social) {
      // era una imagen recién elegida, todavía no subida al servidor: solo cancelo la selección
      this.x_img_credencial_obra_social = undefined;
      this.preview_img_credencial_obra_social.set(this.previewCredencialOriginal);
      return;
    }

    if (!this.previewCredencialOriginal) return;

    await this._loadingService.run(async () => {
      try {
        await firstValueFrom(this._usuarioService.deleteClienteImgCredencialObraSocial());
        const c = { ...(this._usuarioService.cliente() ?? {}) } as any;
        c.x_img_credencial_obra_social = null;
        this._usuarioService.cliente.set(c);
        this.previewCredencialOriginal = '';
        this.preview_img_credencial_obra_social.set('');
      } catch (e) {
        console.error('Error al eliminar la imagen:', e);
        this.error.set('No se pudo eliminar la imagen. Intentá nuevamente.');
      }
    });
  }

  extraerBase64 = async ($event: any) => new Promise((resolve, reject) => {
    try {
      const unsafeImg = window.URL.createObjectURL($event);
      const image = this.sanitizer.bypassSecurityTrustUrl(unsafeImg);
      const reader = new FileReader();
      reader.readAsDataURL($event);
      reader.onload = () => {
        resolve({
          base: reader.result
        });
      };
      reader.onerror = error => {
        resolve({
          base: null
        });
      };
      return;
    } catch (e) {
      return null;
    }
  })

  back(){
    this._location.back();
  }
}
